import { findFrame, type PublicRoomState, type PublicSeat } from '@spin/shared';
import { type CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { game } from '../api/game';
import { Avatar, Spinner, useBackButton } from '../components/common';
import { Emoji } from '../components/Emoji';
import { FrameArt, hasFrameArt } from '../components/FrameArt';
import { reportError, useT } from '../i18n';
import { serverNow, useRoom } from '../store/room';
import { useSession } from '../store/session';
import { useUi } from '../store/ui';
import { tg } from '../tg/bridge';
import { Bottle } from './Bottle';
import { ChatPanel } from './ChatPanel';
import { GameDialogs } from './dialogs';
import { FxLayer } from './FxLayer';
import { BOTTLE_CENTER, drawnAngle, PAIR_POSITIONS, seatPosition, secondsLeft } from './layout';
import { MusicDock } from './MusicBar';
import { TableSheets } from './sheets';
import { sfx } from './sounds';
import { TopBar } from './TopBar';

function useNow(intervalMs = 250): number {
  const [now, setNow] = useState(serverNow());
  useEffect(() => {
    const id = setInterval(() => setNow(serverNow()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

/** The game: the wooden table with its top bar, then music, chat and the message bar. */
export function Table() {
  const room = useRoom((s) => s.room);
  const me = useSession((s) => s.me)!;
  const reset = useSession((s) => s.reset);
  const openSheet = useUi((s) => s.openSheet);

  const exit = useCallback(async () => {
    try {
      await game.leave();
    } catch (err) {
      reportError(err);
    }
    reset({ name: 'portal' });
  }, [reset]);
  useBackButton(() => void exit(), !useUi((s) => s.dialog || s.sheet));

  // Ask before closing Telegram while seated.
  useEffect(() => {
    tg.confirmClose(true);
    return () => tg.confirmClose(false);
  }, []);

  // Opened without a table (Start in the portal): sit down. Before the connection is up, the first sync does it.
  const seated = Boolean(room);
  useEffect(() => {
    if (!seated && game.connected) game.takeSeat().catch(reportError);
  }, [seated]);

  if (!room) {
    return (
      <div className="game">
        <div className="board" data-testid="table-loading">
          <div className="center" style={{ position: 'absolute', inset: 0 }}>
            <Spinner />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="game" data-testid="game">
      <div className="board" data-testid="table" data-phase={room.phase.kind} data-room={room.id}>
        <TopBar room={room} onExit={() => void exit()} />
        <Board
          room={room}
          meId={me.id}
          volume={me.soundVolume / 100}
          onSeat={(seat) => openSheet('player', { userId: seat.userId })}
        />
      </div>
      <div className="below">
        <ChatPanel />
        <MusicDock room={room} />
        <TableSheets />
      </div>
      <GameDialogs />
    </div>
  );
}

/** Green curved arrows from whoever kissed toward the other, with lips on the way. */
function KissArrows({ forward, back }: { forward: boolean; back: boolean }) {
  if (!forward && !back) return null;
  return (
    <svg className="kiss-arrows" viewBox="0 0 140 100" aria-hidden data-testid="kiss-arrows">
      <defs>
        <marker
          id="kiss-head"
          viewBox="0 0 10 10"
          refX="6"
          refY="5"
          markerWidth="4"
          markerHeight="4"
          orient="auto"
        >
          <path d="M0 0 L10 5 L0 10 z" fill="#5fd35f" />
        </marker>
      </defs>
      {forward && (
        <g>
          <path d="M 40 18 Q 70 -10 100 18" markerEnd="url(#kiss-head)" />
          <text x="70" y="9" textAnchor="middle">
            💋
          </text>
        </g>
      )}
      {back && (
        <g>
          <path d="M 100 82 Q 70 110 40 82" markerEnd="url(#kiss-head)" />
          <text x="70" y="99" textAnchor="middle">
            💋
          </text>
        </g>
      )}
    </svg>
  );
}

/** The arrow from the bottle toward the player whose turn it is ("Spins the bottle"). */
function SpinnerArrow({ angle }: { angle: number }) {
  return (
    <span
      className="spinner-arrow"
      style={{ left: `${BOTTLE_CENTER.left}%`, top: `${BOTTLE_CENTER.top}%`, rotate: `${angle}deg` }}
      aria-hidden
    >
      <svg viewBox="0 0 20 30">
        <path d="M10 29 V8" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
        <path d="M3 11 L10 2 L17 11" stroke="#fff" strokeWidth="3" fill="none" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

function SeatCard({
  seat,
  index,
  now,
  style,
  turn,
  inPair,
  onSeat,
}: {
  seat: PublicSeat;
  index: number;
  now: number;
  style: CSSProperties;
  turn: boolean;
  inPair: boolean;
  onSeat: (seat: PublicSeat) => void;
}) {
  const frame = findFrame(seat.frame);
  const ornate = hasFrameArt(frame?.code);
  const live = seat.gifts.filter((g) => g.until > now);
  // A worn gift (hat, crown, glasses) sits on the head and covers the frame's own top decoration.
  const worn = live.filter((g) => g.slot === 'head').at(-1);
  const held = live.filter((g) => g.slot !== 'head').slice(-2);
  const cls = ['seat', turn && 'turn', inPair && 'in-pair', !seat.connected && 'offline']
    .filter(Boolean)
    .join(' ');
  return (
    <button
      className={cls}
      style={style}
      onClick={() => onSeat(seat)}
      aria-label={seat.name}
      data-testid={`seat-${index}`}
    >
      <span
        className={frame ? (ornate ? 'seat-card framed ornate' : 'seat-card framed') : 'seat-card'}
        style={
          frame && !ornate ? { background: frame.ring, boxShadow: `0 0 2.4cqw ${frame.glow}` } : undefined
        }
      >
        <span className="seat-photo">
          <Avatar name={seat.name} photoUrl={seat.photoUrl} size="100%" />
        </span>
        {ornate && frame && <FrameArt code={frame.code} />}
        {worn ? (
          <span className="seat-worn" data-testid={`worn-${index}`}>
            <Emoji e={worn.emoji} />
          </span>
        ) : (
          frame?.top && (
            <span className="frame-top">
              <Emoji e={frame.top} />
            </span>
          )
        )}
        {frame?.corner && (
          <span className="frame-corner">
            <Emoji e={frame.corner} />
          </span>
        )}
        {seat.kisses > 0 && (
          <span className="kiss-badge" data-testid={`kisses-${index}`}>
            {seat.kisses}
          </span>
        )}
        {held.length > 0 && (
          <span className="seat-gifts">
            {held.map((g, k) => (
              <span key={k}>
                <Emoji e={g.emoji} />
              </span>
            ))}
          </span>
        )}
      </span>
      <span className={seat.vip ? 'seat-name vip' : 'seat-name'}>
        {frame?.nameIcon && <i>{frame.nameIcon}</i>}
        <span className="name-text">
          {seat.name}
          {seat.age ? `, ${seat.age}` : ''}
        </span>
        {frame?.nameIcon && <i>{frame.nameIcon}</i>}
      </span>
    </button>
  );
}

function Board({
  room,
  meId,
  volume,
  onSeat,
}: {
  room: PublicRoomState;
  meId: string;
  volume: number;
  onSeat: (seat: PublicSeat) => void;
}) {
  const t = useT();
  const now = useNow();
  const phase = room.phase;
  const seats = room.seats;
  const mySeat = seats.findIndex((s) => s?.userId === meId);
  const lastPhase = useRef<string>('');

  // Sounds and haptics on phase changes.
  useEffect(() => {
    const key = `${phase.kind}:${'roundId' in phase ? phase.roundId : 'turnId' in phase ? phase.turnId : ''}`;
    if (key === lastPhase.current) return;
    lastPhase.current = key;
    if (phase.kind === 'SPINNING') sfx.spin(volume);
    if (phase.kind === 'TURN' && phase.spinnerSeat === mySeat) {
      sfx.turn(volume);
      tg.haptic('medium');
    }
    if (phase.kind === 'DECISION' && (phase.spinnerSeat === mySeat || phase.targetSeat === mySeat))
      tg.haptic('medium');
    if (phase.kind === 'RESULT') {
      const kissed = phase.spinnerChoice === 'KISS' || phase.targetChoice === 'KISS';
      if (kissed) sfx.kiss(volume);
      else sfx.refuse(volume);
    }
  }, [phase, mySeat, volume]);

  const spin = async () => {
    if (phase.kind !== 'TURN') return;
    try {
      tg.haptic('light');
      await game.emit('turn.spin', { turnId: phase.turnId });
    } catch (err) {
      reportError(err);
    }
  };

  const decide = async (choice: 'KISS' | 'REFUSE') => {
    if (phase.kind !== 'DECISION') return;
    try {
      tg.haptic(choice === 'KISS' ? 'success' : 'light');
      await game.emit('round.decide', { roundId: phase.roundId, choice });
    } catch (err) {
      reportError(err);
    }
  };

  const pair =
    phase.kind === 'DECISION' || phase.kind === 'RESULT' ? [phase.spinnerSeat, phase.targetSeat] : null;
  const myRole = pair ? (pair[0] === mySeat ? 'spinner' : pair[1] === mySeat ? 'target' : null) : null;
  const spinnerKissed =
    phase.kind === 'RESULT'
      ? phase.spinnerChoice === 'KISS'
      : phase.kind === 'DECISION' && phase.choices.spinner === 'KISS';
  const targetKissed =
    phase.kind === 'RESULT'
      ? phase.targetChoice === 'KISS'
      : phase.kind === 'DECISION' && phase.choices.target === 'KISS';
  const myTurn = phase.kind === 'TURN' && phase.spinnerSeat === mySeat;

  // The caption in the middle of the table, as in the reference.
  let caption = '';
  let captionAt = { left: 50, top: 37 };
  if (phase.kind === 'WAITING') {
    caption = t('table.waiting_turn');
    captionAt = BOTTLE_CENTER;
  } else if (phase.kind === 'TURN') {
    caption = myTurn ? t('table.spin_the_bottle') : t('table.spins_the_bottle');
    captionAt = { left: 50, top: BOTTLE_CENTER.top + 19 };
  } else if (phase.kind === 'DECISION') {
    const mine = myRole ? phase.choices[myRole] : null;
    const other = myRole ? phase.choices[myRole === 'spinner' ? 'target' : 'spinner'] : null;
    if (myRole && mine === null) caption = other === 'KISS' ? t('table.kiss_back') : t('table.your_choice');
    else caption = spinnerKissed || targetKissed ? t('table.kiss_back') : t('table.will_they');
  }

  const showDecision = phase.kind === 'DECISION' && myRole !== null && phase.choices[myRole] === null;

  return (
    <>
      {seats.map((seat, i) => {
        if (!seat) return null;
        const k = pair ? pair.indexOf(i) : -1;
        const pos = k >= 0 ? PAIR_POSITIONS[k]! : seatPosition(i, room.capacity);
        return (
          <SeatCard
            key={seat.userId}
            seat={seat}
            index={i}
            now={now}
            style={{ left: `${pos.left}%`, top: `${pos.top}%` }}
            turn={phase.kind === 'TURN' && phase.spinnerSeat === i}
            inPair={k >= 0}
            onSeat={onSeat}
          />
        );
      })}
      {!pair && <Bottle room={room} canSpin={myTurn} onSpin={() => void spin()} />}
      {phase.kind === 'TURN' && !myTurn && (
        <SpinnerArrow angle={drawnAngle(phase.spinnerSeat, room.capacity)} />
      )}
      {caption && (
        <span
          className="caption"
          style={{ left: `${captionAt.left}%`, top: `${captionAt.top}%` }}
          data-testid="caption"
        >
          {caption}
        </span>
      )}
      {pair && <KissArrows forward={spinnerKissed} back={targetKissed} />}
      {phase.kind === 'RESULT' && spinnerKissed && targetKissed && (
        <span key={phase.roundId} className="mutual-ring" />
      )}
      {phase.kind === 'DECISION' && (
        <span className="decision-timer outlined" style={{ left: '50%', top: '53.5%' }} data-testid="timer">
          {secondsLeft(phase.deadline, now)}
        </span>
      )}
      {showDecision && (
        <div className="decision">
          <button className="refuse" onClick={() => void decide('REFUSE')} data-testid="refuse">
            {t('table.refuse')}
          </button>
          <button className="kiss" onClick={() => void decide('KISS')} data-testid="kiss">
            {t('table.kiss')}
          </button>
        </div>
      )}
      <FxLayer capacity={room.capacity} />
    </>
  );
}
